import { NavLink, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { LayoutDashboard, ShoppingCart, Receipt, Package, FolderTree, Contact, FileText, CreditCard, Users, BarChart3, Sparkles, Globe, Settings as SettingsIcon, LogOut, Clock, Menu, X } from "lucide-react";
import { useAuth, can } from "@/context/AuthContext";
import api from "@/lib/api";
import { toast } from "sonner";
import ThemeToggle from "@/components/ThemeToggle";

const NAV = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, perm: "dashboard" },
  { to: "/pos", label: "POS", icon: ShoppingCart, perm: "pos" },
  { to: "/orders", label: "Orders", icon: Receipt, perm: "orders" },
  { to: "/products", label: "Products", icon: Package, perm: "products" },
  { to: "/categories", label: "Categories", icon: FolderTree, perm: "products" },
  { to: "/customers", label: "Customers", icon: Contact, perm: "customers" },
  { to: "/invoices", label: "Invoices", icon: FileText, perm: "invoices" },
  { to: "/payments", label: "Payments", icon: CreditCard, perm: "payments" },
  { to: "/employees", label: "Employees", icon: Users, perm: "employees" },
  { to: "/reports", label: "Reports", icon: BarChart3, perm: "reports" },
  { to: "/store", label: "Online Store", icon: Globe, perm: "store" },
  { to: "/ai", label: "AI Assistant", icon: Sparkles, perm: "ai" },
  { to: "/settings", label: "Settings", icon: SettingsIcon, perm: "settings" },
];

export default function AppShell({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [clockedIn, setClockedIn] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => { api.get("/shifts/status").then(({ data }) => setClockedIn(data.clocked_in)).catch(() => {}); }, []);
  const toggleClock = async () => {
    try { const { data } = await api.post("/shifts/clock"); setClockedIn(data.action === "clock_in"); toast.success(data.action === "clock_in" ? "Clocked in" : "Clocked out"); }
    catch { toast.error("Could not update shift"); }
  };
  const doLogout = async () => { await logout(); navigate("/login"); };
  const items = NAV.filter((n) => can(user.role, n.perm));

  return (
    <div className="h-screen flex bg-[#fafafa] dark:bg-[#0d0f13] overflow-hidden">
      {open && <div className="md:hidden fixed inset-0 bg-black/30 z-30" onClick={() => setOpen(false)} />}
      <aside className={`${open ? "flex" : "hidden"} md:flex flex-col w-60 bg-white border-r border-zinc-100 shrink-0 fixed md:relative z-40 h-full`} data-testid="sidebar">
        <div className="h-16 flex items-center gap-2.5 px-4 shrink-0">
          <div className="h-9 w-9 rounded-lg bg-white border border-zinc-200 flex items-center justify-center overflow-hidden shrink-0 p-0.5"><img src="/demodog-logo.jpeg" alt="Demodog World Inc." className="h-full w-full object-contain" /></div>
          <div className="min-w-0 leading-tight">
            <span className="block font-heading font-extrabold text-[15px] truncate">Demodog World Inc.</span>
            <span className="block text-[10px] text-zinc-400 font-semibold tracking-widest uppercase">ERP · Ontario</span>
          </div>
          <button onClick={() => setOpen(false)} className="md:hidden ml-auto h-8 w-8 rounded-lg hover:bg-zinc-100 flex items-center justify-center"><X size={18} /></button>
        </div>
        <nav className="flex-1 px-3 py-2 space-y-0.5 overflow-y-auto">
          {items.map((n) => (
            <NavLink key={n.to} to={n.to} onClick={() => setOpen(false)} data-testid={`nav-${n.perm}`}
              className={({ isActive }) => `relative flex items-center gap-3 h-10 px-3 rounded-lg text-[14px] font-medium transition-colors ${isActive ? "bg-brand-soft text-brand" : "text-zinc-500 hover:bg-zinc-100 hover:text-zinc-900"}`}>
              {({ isActive }) => (<>{isActive && <span className="absolute left-0 top-1/2 -translate-y-1/2 h-5 w-1 rounded-full bg-brand" />}<n.icon size={18} strokeWidth={2} />{n.label}</>)}
            </NavLink>
          ))}
        </nav>
        <div className="p-3 space-y-2 border-t border-zinc-100 shrink-0">
          <ThemeToggle />
          <button onClick={toggleClock} data-testid="clock-toggle" className={`w-full h-10 rounded-lg flex items-center justify-center gap-2 font-semibold text-sm transition active:scale-[0.98] ${clockedIn ? "bg-emerald-50 text-emerald-600" : "text-zinc-600 hover:bg-zinc-100"}`}><Clock size={17} /> {clockedIn ? "Clock Out" : "Clock In"}</button>
          <div className="flex items-center gap-2.5 px-2 py-1.5">
            <div className="h-8 w-8 rounded-full bg-zinc-900 text-white flex items-center justify-center font-semibold text-xs">{user.name.charAt(0)}</div>
            <div className="min-w-0 flex-1"><p className="text-[13px] font-semibold truncate leading-tight">{user.name}</p><p className="text-[11px] text-zinc-400 uppercase tracking-wider">{user.role}</p></div>
            <button onClick={doLogout} data-testid="logout-btn" className="h-8 w-8 rounded-lg text-zinc-400 hover:bg-zinc-100 hover:text-zinc-900 flex items-center justify-center"><LogOut size={17} /></button>
          </div>
        </div>
      </aside>
      <div className="flex-1 flex flex-col min-w-0">
        <header className="md:hidden h-14 bg-white border-b border-zinc-100 flex items-center px-4 gap-3">
          <button onClick={() => setOpen(!open)} data-testid="menu-toggle" className="h-9 w-9 rounded-lg hover:bg-zinc-100 flex items-center justify-center"><Menu size={20} /></button>
          <div className="flex items-center gap-2 min-w-0">
            <div className="h-8 w-8 rounded-lg bg-white border border-zinc-200 flex items-center justify-center overflow-hidden p-0.5 shrink-0"><img src="/demodog-logo.jpeg" alt="Demodog World Inc." className="h-full w-full object-contain" /></div>
            <span className="font-heading font-extrabold text-base truncate">Demodog World Inc.</span>
          </div>
        </header>
        <main className="flex-1 overflow-hidden">{children}</main>
      </div>
    </div>
  );
}
